import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, Loader2, ShieldCheck, Flag, UserX, MessageCircleHeart, Sparkles, Wifi, WifiOff, LogOut, Bell, BellOff, SmilePlus, Languages, EyeOff, ChevronDown, Lock, X as XIcon } from "lucide-react";
import confetti from "canvas-confetti";

const REACTION_EMOJIS = ["👍", "❤️", "🤗", "🙌", "💪", "✨"];

// Supported translation languages — matches backend ALLOWED_TRANSLATE_LANGS
const TRANSLATE_LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
];
const CONFETTI_THRESHOLD = 5;  // total reactions needed to trigger confetti

const POLL_MS = 4000; // refresh chat messages every 4s
const HEARTBEAT_MS = 30000; // ping presence every 30s when online

// Play a soft, short "pop" tone via Web Audio API (no external file)
function playPop() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.22);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
    osc.onended = () => ctx.close().catch(() => {});
  } catch { /* silent */ }
}

function formatRelativeTime(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 5) return "à l'instant";
    if (diff < 60) return `il y a ${Math.floor(diff)} s`;
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function AccessibilityChatRoom() {
  const [messages, setMessages] = useState([]);
  const [pseudo, setPseudo] = useState("");
  const [avatar, setAvatar] = useState("");
  const [avatarOptions, setAvatarOptions] = useState([]);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Presence: 3 states
  // - "online"  → polling + heartbeat (visible to others, see updates)
  // - "offline" → no polling, no heartbeat (invisible, frozen view, can still scroll/read)
  // - "left"    → completely disconnected from chat (input hidden, prompt to rejoin)
  const [presence, setPresence] = useState("online");
  const [onlineCount, setOnlineCount] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("acc_chat_sound") !== "off"; } catch { return true; }
  });
  const lastMessageIdsRef = useRef(new Set()); // tracks message ids we've already seen (for sound)
  const messagesRef = useRef(null);
  const pollTimer = useRef(null);
  const heartbeatTimer = useRef(null);

  // Security banner — dismissible "do not show today" + first-visit popup
  // Stored as ISO date string (YYYY-MM-DD). If saved date == today, hide banner.
  const _todayStr = () => new Date().toISOString().slice(0, 10);
  const [secBanner, setSecBanner] = useState(() => {
    try { return localStorage.getItem("acc_chat_security_banner_hide_until") !== _todayStr(); } catch { return true; }
  });
  // First-time popup — shown only once per browser (then forever dismissed)
  const [secPopup, setSecPopup] = useState(() => {
    try { return !localStorage.getItem("acc_chat_security_popup_v1"); } catch { return false; }
  });

  const dismissBannerToday = () => {
    try { localStorage.setItem("acc_chat_security_banner_hide_until", _todayStr()); } catch { /* ignore */ }
    setSecBanner(false);
  };
  const closeSecPopup = () => {
    try { localStorage.setItem("acc_chat_security_popup_v1", "1"); } catch { /* ignore */ }
    setSecPopup(false);
  };

  const scrollToBottom = (smooth = true) => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  const fetchMe = async () => {
    try {
      const { data } = await api.get("/accessibility/chat-room/me");
      setPseudo(data.pseudo || "");
      setAvatar(data.avatar || "");
      setAvatarOptions(data.avatar_options || []);
    } catch { /* silent */ }
  };

  const chooseAvatar = async (emoji) => {
    if (emoji === avatar) { setAvatarPickerOpen(false); return; }
    try {
      await api.post("/accessibility/chat-room/avatar", { avatar: emoji });
      setAvatar(emoji);
      setAvatarPickerOpen(false);
      toast.success(`Avatar mis à jour ${emoji}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du changement d'avatar");
    }
  };

  const markRead = async () => {
    try { await api.post("/accessibility/chat-room/mark-read"); } catch { /* silent */ }
  };

  const fetchMessages = async (initial = false) => {
    try {
      const { data } = await api.get("/accessibility/chat-room/messages");
      const newMessages = data.messages || [];
      // Detect new incoming messages (not own, not already seen, only after initial load)
      if (!initial && presence === "online" && soundOn && !document.hidden) {
        const hasNewFromOther = newMessages.some(
          (m) => !m.is_own && !lastMessageIdsRef.current.has(m.id)
        );
        if (hasNewFromOther) playPop();
      }
      lastMessageIdsRef.current = new Set(newMessages.map((m) => m.id));
      setMessages(newMessages);
      setError("");
      if (initial) setTimeout(() => scrollToBottom(false), 50);
    } catch (e) {
      if (initial) setError("Impossible de charger les messages. Réessaie dans un instant.");
    } finally {
      if (initial) setLoading(false);
    }
  };

  const toggleSound = () => {
    setSoundOn((s) => {
      const next = !s;
      try { localStorage.setItem("acc_chat_sound", next ? "on" : "off"); } catch { /* silent */ }
      if (next) playPop();  // give immediate audio feedback so the user knows it works
      toast.info(next ? "Notifications sonores activées 🔔" : "Notifications sonores désactivées 🔕");
      return next;
    });
  };

  const fetchOnlineCount = async () => {
    try {
      const { data } = await api.get("/accessibility/chat-room/presence");
      setOnlineCount(data.online_count || 0);
    } catch { /* silent */ }
  };

  const sendHeartbeat = async () => {
    try { await api.post("/accessibility/chat-room/presence"); } catch { /* silent */ }
  };

  const stopTimers = () => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    if (heartbeatTimer.current) { clearInterval(heartbeatTimer.current); heartbeatTimer.current = null; }
  };

  // Manage polling + heartbeat lifecycle based on presence state
  // - "online"   → poll messages + send heartbeat (counted in the "X online" badge)
  // - "offline"  → poll messages but NO heartbeat (invisible: read/write OK, NOT counted) — RGPD-friendly
  // - "left"     → no polling, no heartbeat, no form
  useEffect(() => {
    stopTimers();
    if (presence === "online") {
      sendHeartbeat();
      fetchOnlineCount();
      pollTimer.current = setInterval(() => {
        fetchMessages();
        fetchOnlineCount();
        markRead();
      }, POLL_MS);
      heartbeatTimer.current = setInterval(sendHeartbeat, HEARTBEAT_MS);
    } else if (presence === "offline") {
      // Invisible mode: still fetch new messages, but never send heartbeat so the user isn't counted
      fetchOnlineCount();
      pollTimer.current = setInterval(() => {
        fetchMessages();
        fetchOnlineCount();
        markRead();
      }, POLL_MS);
    }
    return stopTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presence]);

  useEffect(() => {
    fetchMe();
    fetchMessages(true).then(() => markRead());
    // If coming from the dashboard intro CTA, pre-fill the input + scroll to chat
    try {
      const prefill = sessionStorage.getItem("acc_chat_prefill");
      if (prefill) {
        setInput(prefill);
        sessionStorage.removeItem("acc_chat_prefill");
        setTimeout(() => {
          const el = document.getElementById("acc-chat");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
      }
    } catch { /* silent */ }
    // Cleanup: mark offline when component unmounts or page closes
    const handleUnload = () => {
      try { navigator.sendBeacon?.(`${process.env.REACT_APP_BACKEND_URL}/api/accessibility/chat-room/presence/leave`); } catch {}
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      // Mark offline on unmount
      api.delete("/accessibility/chat-room/presence").catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll when new messages arrive (only if user is already near bottom)
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (nearBottom) setTimeout(() => scrollToBottom(), 50);
  }, [messages]);

  const goOnline = async () => {
    setPresence("online");
    toast.success("Tu es en ligne — visible aux autres.");
    await fetchMessages();
  };

  const goOffline = () => {
    setPresence("offline");
    api.delete("/accessibility/chat-room/presence").catch(() => {});
    toast.info("Mode invisible activé — tu peux lire et écrire, mais tu n'apparais plus dans le compteur en ligne (RGPD).");
  };

  const leaveChat = () => {
    if (!window.confirm("Quitter le chat ? Tu ne recevras plus de nouveaux messages tant que tu ne reviens pas.")) return;
    setPresence("left");
    api.delete("/accessibility/chat-room/presence").catch(() => {});
    toast.success("Tu as quitté le chat.");
  };

  const confettiFiredRef = useRef(new Set());  // tracks msg ids that already fired confetti (one-shot)

  const fireConfettiAt = (rect) => {
    try {
      const x = rect ? (rect.left + rect.width / 2) / window.innerWidth : 0.5;
      const y = rect ? Math.max(0.1, rect.top / window.innerHeight) : 0.5;
      confetti({
        particleCount: 80,
        spread: 70,
        startVelocity: 30,
        origin: { x, y },
        colors: ["#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#ef4444"],
        scalar: 0.9,
        ticks: 120,
      });
    } catch { /* silent */ }
  };

  const react = async (msgId, emoji) => {
    // Optimistic update: toggle locally first for instant feedback
    setMessages((prev) => prev.map((m) => {
      if (m.id !== msgId) return m;
      const list = [...(m.reactions || [])];
      const idx = list.findIndex((r) => r.emoji === emoji);
      if (idx >= 0) {
        const r = list[idx];
        const nextCount = r.count + (r.mine ? -1 : 1);
        if (nextCount <= 0) list.splice(idx, 1);
        else list[idx] = { ...r, count: nextCount, mine: !r.mine };
      } else {
        list.push({ emoji, count: 1, mine: true });
      }
      return { ...m, reactions: list };
    }));
    try {
      const { data } = await api.post(`/accessibility/chat-room/message/${msgId}/react`, { emoji });
      // Reconcile with server response (authoritative)
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, reactions: data.reactions || [] } : m)));
      // Fire confetti when message crosses the popularity threshold (one shot per session)
      const totalReactions = (data.reactions || []).reduce((s, r) => s + (r.count || 0), 0);
      if (totalReactions >= CONFETTI_THRESHOLD && !confettiFiredRef.current.has(msgId)) {
        confettiFiredRef.current.add(msgId);
        const el = document.querySelector(`[data-testid="acc-chat-msg-${msgId}"]`);
        fireConfettiAt(el?.getBoundingClientRect());
      }
    } catch (e) {
      // Revert by refetching
      fetchMessages();
      toast.error("Erreur, réessaie");
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    if (text.length > 500) { toast.error("Message trop long (500 max)"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/accessibility/chat-room/message", { text });
      // Optimistic append (de-dup against poll)
      setMessages((m) => {
        if (m.some((x) => x.id === data.id)) return m;
        return [...m, data];
      });
      setInput("");
      setTimeout(() => scrollToBottom(), 50);
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur d'envoi";
      toast.error(typeof msg === "string" ? msg : "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const report = async (msgId) => {
    if (!window.confirm("Signaler ce message à la modération ? Cette action est anonyme.")) return;
    try {
      const { data } = await api.post(`/accessibility/chat-room/message/${msgId}/report`, {});
      if (data.already_reported) toast.info("Tu as déjà signalé ce message.");
      else if (data.auto_hidden) toast.success("Message signalé et masqué. Merci !");
      else toast.success("Message signalé. Merci pour ta vigilance.");
      fetchMessages();
    } catch {
      toast.error("Erreur lors du signalement");
    }
  };

  const block = async (msgId, pseudoToBlock) => {
    if (!window.confirm(`Bloquer ${pseudoToBlock} ? Tu ne verras plus jamais ses messages dans ce chat.`)) return;
    try {
      await api.post(`/accessibility/chat-room/message/${msgId}/block`, {});
      toast.success(`${pseudoToBlock} bloqué. Tu ne verras plus ses messages.`);
      fetchMessages();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <section id="acc-chat" data-testid="acc-chatroom">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-700 font-semibold mb-2">Communauté bienveillante · Chat anonyme</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Parle avec d'autres étudiant·e·s, en toute confiance</h2>
        <p className="text-slate-600 mt-2 max-w-2xl">Un espace d'entraide en temps réel. Aucun nom réel n'apparaît : tu es identifié·e uniquement par un pseudo généré aléatoirement.</p>
      </header>

      {/* Security banner — dismissible "do not show today" */}
      {secBanner && (
        <div
          className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 sm:p-5 mb-4 relative"
          role="region"
          aria-label="Conseil de sécurité"
          data-testid="acc-chat-security-banner"
        >
          <button
            onClick={dismissBannerToday}
            className="absolute top-3 right-3 w-7 h-7 rounded-full hover:bg-amber-100 flex items-center justify-center text-amber-700"
            aria-label="Masquer ce bandeau pour aujourd'hui"
            data-testid="acc-chat-security-banner-close"
            title="Ne plus afficher aujourd'hui"
          >
            <XIcon size={14} />
          </button>
          <div className="flex items-start gap-3 pr-8">
            <div className="w-9 h-9 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center shrink-0">
              <Lock size={16} className="text-amber-700" />
            </div>
            <div className="text-sm text-amber-900 leading-relaxed">
              <p className="font-semibold mb-1">Conseil de sécurité</p>
              <p>
                Pour ta sécurité, ne partage <strong>jamais</strong> de mot de passe, coordonnées bancaires, documents sensibles, ni d'informations personnelles importantes dans le chat.
              </p>
              <p className="mt-1.5 text-xs text-amber-800/90">
                Student Hub France est une plateforme communautaire et ne peut être tenue responsable des informations échangées entre utilisateurs. En utilisant le chat, tu acceptes de rester vigilant·e et respectueux·se.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Charter / rules */}
      <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-4 sm:p-5 text-sm text-violet-900 leading-relaxed mb-4" data-testid="acc-chat-charter">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} className="text-violet-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-1">Charte de bienveillance</p>
            <ul className="list-disc pl-5 space-y-0.5 text-violet-800">
              <li>Respect, écoute, zéro jugement.</li>
              <li>Pas d'insulte, pas de harcèlement, pas de contenu sexuel ou violent.</li>
              <li>Tu peux <strong>signaler</strong> ou <strong>bloquer</strong> n'importe quel message. 3 signalements = message auto-masqué.</li>
              <li>Pour une urgence psychologique : 3114 (24/24) · Nightline 05 82 95 10 11 (21h-2h30).</li>
            </ul>
          </div>
        </div>
      </div>

      {/* My pseudo + presence controls + online count */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3" data-testid="acc-chat-statusbar">
        {pseudo && (
          <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="acc-chat-pseudo">
            <button
              onClick={() => setAvatarPickerOpen((v) => !v)}
              className="relative w-9 h-9 rounded-full bg-violet-50 border-2 border-violet-200 hover:border-violet-400 flex items-center justify-center text-xl transition-all"
              aria-label="Changer mon avatar"
              data-testid="acc-chat-avatar-btn"
            >
              <span aria-hidden="true">{avatar || "🌟"}</span>
              <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white border border-slate-200 text-[9px] flex items-center justify-center text-slate-500">✏️</span>
            </button>
            <span className="font-semibold text-violet-700 px-2 py-0.5 rounded-md bg-violet-50">{pseudo}</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-500" data-testid="acc-chat-online-count">
              <span className={`inline-block w-2 h-2 rounded-full ${onlineCount > 0 && presence === "online" ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
              {onlineCount} en ligne
            </span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button onClick={toggleSound} className="text-xs px-3 py-1.5 rounded-full bg-white text-slate-600 border border-slate-200 hover:border-violet-300 hover:text-violet-600 inline-flex items-center gap-1.5" data-testid="acc-chat-btn-sound" aria-label={soundOn ? "Désactiver le son" : "Activer le son"} aria-pressed={soundOn}>
            {soundOn ? <Bell size={12} /> : <BellOff size={12} />}
            <span className="hidden sm:inline">{soundOn ? "Son" : "Muet"}</span>
          </button>
          {/* Presence selector — clear 3-state menu (online / invisible / leave) */}
          <PresenceMenu
            presence={presence}
            onGoOnline={goOnline}
            onGoInvisible={goOffline}
            onLeave={leaveChat}
          />
        </div>
      </div>

      {/* Avatar picker dropdown */}
      {avatarPickerOpen && avatarOptions.length > 0 && (
        <div className="rounded-2xl border border-violet-200 bg-white p-4 mb-4 shadow-sm" data-testid="acc-chat-avatar-picker">
          <p className="text-xs font-semibold text-slate-700 mb-3">Choisis ton avatar — il sera visible dans tes messages</p>
          <div className="grid grid-cols-8 gap-2">
            {avatarOptions.map((emoji) => (
              <button
                key={emoji}
                onClick={() => chooseAvatar(emoji)}
                className={`aspect-square rounded-xl border-2 text-2xl flex items-center justify-center transition-all hover:scale-110 ${
                  emoji === avatar
                    ? "border-violet-500 bg-violet-50 shadow-sm"
                    : "border-slate-200 hover:border-violet-300 bg-white"
                }`}
                aria-label={`Avatar ${emoji}`}
                aria-pressed={emoji === avatar}
                data-testid={`acc-chat-avatar-option-${emoji}`}
              >
                <span aria-hidden="true">{emoji}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col" style={{ minHeight: 480, maxHeight: 600 }}>
        <div ref={messagesRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-3" data-testid="acc-chat-messages">
          {loading && (
            <div className="text-center text-slate-400 text-sm py-8">
              <Loader2 size={18} className="inline animate-spin mr-2" /> Chargement des messages…
            </div>
          )}
          {!loading && error && (
            <div className="text-center text-red-500 text-sm py-8" data-testid="acc-chat-error">{error}</div>
          )}
          {!loading && !error && messages.length === 0 && (
            <div className="text-center text-slate-400 text-sm py-12">
              <MessageCircleHeart size={32} className="mx-auto mb-2 text-violet-300" />
              <p>Aucun message pour le moment.</p>
              <p className="mt-1">Sois le·la premier·ère à briser la glace 💜</p>
            </div>
          )}
          {messages.map((m) => (
            <ChatBubble key={m.id} msg={m} onReport={() => report(m.id)} onBlock={() => block(m.id, m.pseudo)} onReact={(emoji) => react(m.id, emoji)} />
          ))}
        </div>

        {presence === "left" ? (
          <div className="border-t border-slate-100 p-5 flex flex-col items-center gap-3 bg-slate-50" data-testid="acc-chat-left-state">
            <p className="text-sm text-slate-600 text-center">Tu as quitté le chat. Les messages affichés ci-dessus sont en mode lecture.</p>
            <Button onClick={goOnline} className="h-10 px-5 rounded-full bg-violet-600 hover:bg-violet-700" data-testid="acc-chat-rejoin">
              <Wifi size={14} className="mr-2" /> Rejoindre la discussion
            </Button>
          </div>
        ) : (
          <>
            {presence === "offline" && (
              <div className="border-t border-amber-100 px-4 py-2 bg-amber-50 text-xs text-amber-800 flex items-center gap-2" data-testid="acc-chat-offline-banner">
                <EyeOff size={12} /> Mode invisible · tu peux lire et écrire, mais tu n'apparais pas dans le compteur "en ligne". <button onClick={goOnline} className="underline font-semibold ml-1">Redevenir visible</button>
              </div>
            )}
            <form onSubmit={send} className="border-t border-slate-100 p-3 sm:p-4 flex items-end gap-2" data-testid="acc-chat-form">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                maxLength={500}
                placeholder="Ton message (Entrée pour envoyer)…"
                className="flex-1 resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-violet-400"
                data-testid="acc-chat-input"
              />
              <span className="text-xs text-slate-400 self-center hidden sm:block tabular-nums">{input.length}/500</span>
              <Button type="submit" disabled={sending || !input.trim()} className="h-11 px-4 rounded-full bg-violet-600 hover:bg-violet-700" data-testid="acc-chat-send">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </form>
            <p className="px-4 pb-3 text-[11px] text-slate-400 inline-flex items-center gap-1.5" data-testid="acc-chat-input-hint">
              <Lock size={10} /> Ne partage jamais d'informations sensibles (mot de passe, coordonnées bancaires…) dans le chat.
            </p>
          </>
        )}
      </div>

      <p className="text-xs text-slate-500 mt-3 leading-relaxed">
        Les messages plus anciens que 30 jours sont automatiquement supprimés. Tu peux supprimer ton compte (et tous tes messages) à tout moment depuis tes Paramètres.
      </p>

      {/* First-time security popup — shown only once per browser */}
      {secPopup && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-labelledby="acc-chat-secpopup-title"
          data-testid="acc-chat-security-popup"
        >
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl p-7 sm:p-8 text-center animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto shadow-lg shadow-amber-500/30 mb-5">
              <Lock size={26} className="text-white" />
            </div>
            <h2 id="acc-chat-secpopup-title" className="font-display text-2xl font-bold text-slate-900">
              Votre sécurité avant tout 🔒
            </h2>
            <p className="text-slate-600 mt-3 leading-relaxed text-sm">
              Sois prudent·e dans tes échanges et ne partage <strong>jamais</strong> d'informations sensibles ou confidentielles :
            </p>
            <ul className="text-left text-sm text-slate-700 mt-4 space-y-1.5 max-w-xs mx-auto">
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">•</span> Mot de passe</li>
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">•</span> Coordonnées bancaires ou RIB</li>
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">•</span> Documents officiels (CNI, passeport…)</li>
              <li className="flex items-start gap-2"><span className="text-amber-600 mt-0.5">•</span> Informations personnelles sensibles</li>
            </ul>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              Student Hub France est une plateforme communautaire et ne peut être tenue responsable des contenus échangés entre utilisateurs.
            </p>
            <button
              onClick={closeSecPopup}
              className="mt-6 w-full px-6 py-3 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-sm shadow-md transition-all"
              data-testid="acc-chat-security-popup-ok"
              autoFocus
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function ChatBubble({ msg, onReport, onBlock, onReact }) {
  const isOwn = !!msg.is_own;
  const avatar = msg.avatar || "🌟";
  const reactions = msg.reactions || [];
  const [pickerOpen, setPickerOpen] = useState(false);
  // Translation state (per-bubble, local)
  const [translation, setTranslation] = useState(null);   // { lang, text } | null
  const [translating, setTranslating] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false); // when a translation is loaded, allow toggling back to original
  const [langPickerOpen, setLangPickerOpen] = useState(false);

  const handleTranslate = async (code) => {
    setLangPickerOpen(false);
    // Toggle off if user picks the language already shown
    if (translation && translation.lang === code && !showOriginal) {
      setShowOriginal(true);
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post(`/accessibility/chat-room/message/${msg.id}/translate`, { lang: code });
      setTranslation({ lang: code, text: data.translated_text });
      setShowOriginal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de traduction, réessaie.");
    } finally {
      setTranslating(false);
    }
  };

  const displayedText = translation && !showOriginal ? translation.text : msg.text;
  const displayedLangMeta = translation ? TRANSLATE_LANGS.find((l) => l.code === translation.lang) : null;

  return (
    <div className={`flex gap-2 ${isOwn ? "justify-end" : "justify-start"}`} data-testid={`acc-chat-msg-${msg.id}`}>
      {!isOwn && (
        <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-lg shrink-0" aria-hidden="true">{avatar}</div>
      )}
      <div className={`max-w-[80%] ${isOwn ? "items-end" : "items-start"} flex flex-col gap-1 group relative`}>
        <div className="flex items-center gap-2 text-[11px] text-slate-500 px-1">
          <span className={`font-semibold ${isOwn ? "text-violet-700" : "text-slate-700"}`}>{msg.pseudo}</span>
          <span>·</span>
          <span>{formatRelativeTime(msg.created_at)}</span>
          {/* Discrete translate trigger next to the timestamp */}
          <button
            type="button"
            onClick={() => setLangPickerOpen((v) => !v)}
            className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
            aria-label="Traduire ce message"
            data-testid={`acc-chat-translate-btn-${msg.id}`}
          >
            {translating ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
          </button>
          {translation && !showOriginal && displayedLangMeta && (
            <span className="inline-flex items-center gap-1 text-[10px] text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-full" data-testid={`acc-chat-translated-badge-${msg.id}`}>
              <span aria-hidden="true">{displayedLangMeta.flag}</span>
              <span>Traduit</span>
            </span>
          )}
        </div>
        {/* Language picker popover */}
        {langPickerOpen && (
          <div
            className={`absolute z-30 top-6 ${isOwn ? "right-0" : "left-0"} bg-white border border-slate-200 rounded-2xl py-2 shadow-lg w-44`}
            onMouseLeave={() => setLangPickerOpen(false)}
            data-testid={`acc-chat-lang-picker-${msg.id}`}
          >
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 pb-1">Traduire en…</p>
            <div className="max-h-60 overflow-y-auto">
              {TRANSLATE_LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => handleTranslate(l.code)}
                  className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-violet-50 ${
                    translation && translation.lang === l.code ? "bg-violet-50 text-violet-700 font-semibold" : "text-slate-700"
                  }`}
                  data-testid={`acc-chat-lang-option-${msg.id}-${l.code}`}
                >
                  <span aria-hidden="true">{l.flag}</span>
                  <span>{l.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="relative">
          <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line break-words ${
            isOwn
              ? "bg-violet-600 text-white rounded-br-md"
              : "bg-slate-100 text-slate-900 rounded-bl-md"
          }`} data-testid={`acc-chat-msg-text-${msg.id}`}>
            {displayedText}
          </div>
          {/* Toggle between original and translation */}
          {translation && (
            <button
              type="button"
              onClick={() => setShowOriginal((v) => !v)}
              className={`mt-1 text-[11px] underline ${isOwn ? "text-violet-200 hover:text-white" : "text-violet-600 hover:text-violet-800"}`}
              data-testid={`acc-chat-toggle-original-${msg.id}`}
            >
              {showOriginal ? "Voir la traduction" : "Voir l'original"}
            </button>
          )}
          {/* Reaction trigger button — visible on hover/focus */}
          <button
            onClick={() => setPickerOpen((v) => !v)}
            className={`absolute -top-3 ${isOwn ? "left-0 -translate-x-full" : "right-0 translate-x-full"} w-7 h-7 rounded-full bg-white border border-slate-200 shadow-sm hover:border-violet-300 hover:text-violet-600 text-slate-500 inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity`}
            aria-label="Ajouter une réaction"
            data-testid={`acc-chat-react-btn-${msg.id}`}
          >
            <SmilePlus size={14} />
          </button>
          {/* Reaction picker popover */}
          {pickerOpen && (
            <div
              className={`absolute z-20 -top-12 ${isOwn ? "right-0" : "left-0"} bg-white border border-slate-200 rounded-full px-2 py-1 shadow-md flex items-center gap-0.5`}
              data-testid={`acc-chat-react-picker-${msg.id}`}
              onMouseLeave={() => setPickerOpen(false)}
            >
              {REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => { onReact(emoji); setPickerOpen(false); }}
                  className="w-8 h-8 rounded-full text-lg hover:bg-slate-100 transition-all hover:scale-125"
                  aria-label={`Réagir avec ${emoji}`}
                  data-testid={`acc-chat-react-${msg.id}-${emoji}`}
                >
                  <span aria-hidden="true">{emoji}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Existing reactions chips */}
        {reactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 ${isOwn ? "justify-end" : "justify-start"} px-0.5`} data-testid={`acc-chat-reactions-${msg.id}`}>
            {reactions.map((r) => (
              <button
                key={r.emoji}
                onClick={() => onReact(r.emoji)}
                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all ${
                  r.mine
                    ? "bg-violet-50 border-violet-300 text-violet-700"
                    : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                }`}
                data-testid={`acc-chat-reaction-chip-${msg.id}-${r.emoji}`}
                aria-pressed={r.mine}
              >
                <span aria-hidden="true">{r.emoji}</span>
                <span className="font-medium tabular-nums">{r.count}</span>
              </button>
            ))}
          </div>
        )}
        {!isOwn && (
          <div className="flex items-center gap-3 text-[11px] px-1 pt-0.5">
            <button onClick={onReport} className="text-slate-400 hover:text-red-500 inline-flex items-center gap-1" data-testid={`acc-chat-report-${msg.id}`}>
              <Flag size={11} /> Signaler
            </button>
            <button onClick={onBlock} className="text-slate-400 hover:text-red-500 inline-flex items-center gap-1" data-testid={`acc-chat-block-${msg.id}`}>
              <UserX size={11} /> Bloquer
            </button>
          </div>
        )}
      </div>
      {isOwn && (
        <div className="w-8 h-8 rounded-full bg-violet-50 border border-violet-200 flex items-center justify-center text-lg shrink-0" aria-hidden="true">{avatar}</div>
      )}
    </div>
  );
}

// Presence selector — clearer than 2 separate buttons. 3 explicit options:
// - 🟢 "En ligne" : visible, comptabilisé dans "X en ligne"
// - ⚪ "Invisible" : tu peux lire & écrire mais tu n'es PAS comptabilisé·e (RGPD-friendly)
// - 👋 "Quitter" : déconnexion totale du chat
function PresenceMenu({ presence, onGoOnline, onGoInvisible, onLeave }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const isOnline = presence === "online";
  const isInvisible = presence === "offline";

  // Close on outside click / ESC — critical for mobile UX
  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const triggerStyle = isOnline
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
    : isInvisible
    ? "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
    : "bg-white text-slate-600 border-slate-200";

  const label = isOnline ? "En ligne" : isInvisible ? "Invisible" : "Statut";
  const Icon = isOnline ? Wifi : isInvisible ? EyeOff : WifiOff;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${triggerStyle}`}
        data-testid="acc-chat-presence-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change ton statut de présence"
      >
        <Icon size={12} />
        <span>{label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          {/* Mobile backdrop — makes the menu feel like a bottom-sheet on small screens */}
          <div
            className="fixed inset-0 z-40 bg-slate-900/40 sm:hidden"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed left-0 right-0 bottom-0 sm:absolute sm:left-auto sm:right-0 sm:bottom-auto sm:top-9 z-50 sm:w-72 bg-white rounded-t-3xl sm:rounded-2xl border border-slate-200 shadow-2xl sm:shadow-xl p-2 sm:py-1.5 max-h-[85vh] overflow-y-auto"
            role="menu"
            data-testid="acc-chat-presence-menu"
          >
            {/* Mobile drag handle */}
            <div className="sm:hidden flex justify-center py-2" aria-hidden="true">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>
            <div className="sm:hidden px-3 pb-2 pt-1 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">Mon statut</p>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-slate-700 text-2xl leading-none px-2"
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            <button
              type="button"
              onClick={() => { onGoOnline(); setOpen(false); }}
              className={`w-full text-left px-3 py-3 sm:py-2.5 rounded-xl sm:rounded-none hover:bg-emerald-50 flex items-start gap-2.5 ${isOnline ? "bg-emerald-50/60" : ""}`}
              data-testid="acc-chat-presence-online"
              role="menuitemradio"
              aria-checked={isOnline}
            >
              <span className="mt-0.5"><Wifi size={16} className="text-emerald-600" /></span>
              <span className="flex-1">
                <span className="block text-sm font-semibold text-slate-900">En ligne</span>
                <span className="block text-xs text-slate-500 leading-snug mt-0.5">Tu es visible aux autres et comptabilisé·e dans le compteur "en ligne".</span>
              </span>
              {isOnline && <span className="text-emerald-600 text-sm mt-0.5">●</span>}
            </button>
            <button
              type="button"
              onClick={() => { onGoInvisible(); setOpen(false); }}
              className={`w-full text-left px-3 py-3 sm:py-2.5 rounded-xl sm:rounded-none hover:bg-slate-50 flex items-start gap-2.5 ${isInvisible ? "bg-slate-50" : ""}`}
              data-testid="acc-chat-presence-invisible"
              role="menuitemradio"
              aria-checked={isInvisible}
            >
              <span className="mt-0.5"><EyeOff size={16} className="text-slate-500" /></span>
              <span className="flex-1">
                <span className="block text-sm font-semibold text-slate-900">Invisible</span>
                <span className="block text-xs text-slate-500 leading-snug mt-0.5">Tu peux lire et écrire mais tu n'apparais PAS dans le compteur "en ligne" (RGPD).</span>
              </span>
              {isInvisible && <span className="text-slate-500 text-sm mt-0.5">●</span>}
            </button>
            <div className="my-1 border-t border-slate-100" />
            <button
              type="button"
              onClick={() => { onLeave(); setOpen(false); }}
              className="w-full text-left px-3 py-3 sm:py-2.5 rounded-xl sm:rounded-none hover:bg-red-50 flex items-start gap-2.5 mb-2 sm:mb-0"
              data-testid="acc-chat-presence-leave"
              role="menuitem"
            >
              <span className="mt-0.5"><LogOut size={16} className="text-red-500" /></span>
              <span className="flex-1">
                <span className="block text-sm font-semibold text-slate-900">Quitter le chat</span>
                <span className="block text-xs text-slate-500 leading-snug mt-0.5">Déconnexion complète. Tu ne reçois plus de nouveaux messages.</span>
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
